import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';

/**
 * Upload a service log image to Firebase Storage
 * Returns the download URL
 */
export async function uploadServiceLogImage(
  blob: Blob,
  logId: string
): Promise<string> {
  // Generate a unique filename
  const timestamp = Date.now();
  const extension = getExtensionFromMime(blob.type);
  const filename = `service-logs/${logId}_${timestamp}.${extension}`;

  const storageRef = ref(storage, filename);

  // Upload the blob
  const snapshot = await uploadBytes(storageRef, blob, {
    contentType: blob.type,
    customMetadata: {
      logId,
      uploadedAt: new Date().toISOString(),
    },
  });

  // Get the download URL
  const downloadUrl = await getDownloadURL(snapshot.ref);
  return downloadUrl;
}

/**
 * Upload a QA report photo to Firebase Storage
 * Returns the download URL
 */
export async function uploadQAReportPhoto(
  blob: Blob,
  reportId: string,
  photoIndex: number
): Promise<string> {
  // Generate a unique filename
  const timestamp = Date.now();
  const extension = getExtensionFromMime(blob.type);
  const filename = `qa-reports/${reportId}_${photoIndex}_${timestamp}.${extension}`;

  const storageRef = ref(storage, filename);

  // Upload the blob
  const snapshot = await uploadBytes(storageRef, blob, {
    contentType: blob.type,
    customMetadata: {
      reportId,
      photoIndex: String(photoIndex),
      uploadedAt: new Date().toISOString(),
    },
  });

  // Get the download URL
  const downloadUrl = await getDownloadURL(snapshot.ref);
  return downloadUrl;
}

/**
 * Upload an inventory photo to Firebase Storage
 * Returns the download URL
 */
export async function uploadInventoryPhoto(
  blob: Blob,
  entityType: 'container' | 'plant' | 'location',
  entityId: string
): Promise<string> {
  // Generate a unique filename
  const timestamp = Date.now();
  const extension = getExtensionFromMime(blob.type);
  const filename = `inventory/${entityType}/${entityId}_${timestamp}.${extension}`;

  const storageRef = ref(storage, filename);

  // Upload the blob
  const snapshot = await uploadBytes(storageRef, blob, {
    contentType: blob.type,
    customMetadata: {
      entityType,
      entityId,
      uploadedAt: new Date().toISOString(),
    },
  });

  // Get the download URL
  const downloadUrl = await getDownloadURL(snapshot.ref);
  return downloadUrl;
}

/**
 * Compress an image before upload
 * Reduces file size while maintaining quality
 */
export async function compressImage(
  file: File | Blob,
  maxWidth: number = 1920,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate new dimensions
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      // Create canvas and draw image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Could not create blob'));
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image'));
    };

    img.src = url;
  });
}

/**
 * Capture image from camera
 * Returns a File object ready for upload
 */
export async function captureFromCamera(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use rear camera

    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      resolve(file || null);
    };

    input.oncancel = () => {
      resolve(null);
    };

    input.click();
  });
}

/**
 * Get file extension from MIME type
 */
function getExtensionFromMime(mimeType: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/heic': 'heic',
  };
  return map[mimeType] || 'jpg';
}

/**
 * Check if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Get file size in human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
